import React, { useMemo } from 'react';

/**
 * Multi-select organization + site assignment for add/invite/edit user flows.
 * Matches POST/PATCH /users/ scope fields from the backend.
 */
export default function UserScopeAssignFields({
  organizations = [],
  sites = [],
  allOrganizationsAccess = false,
  allSitesAccess = false,
  organizationAccessUuids = [],
  siteAccessUuids = [],
  orgSearch = '',
  siteSearch = '',
  onOrgSearchChange,
  onSiteSearchChange,
  onChange,
  orgSearchId = 'user-scope-org-search',
  siteSearchId = 'user-scope-site-search',
  disabled = false,
}) {
  const selectedOrgIds = useMemo(
    () => new Set((organizationAccessUuids || []).map((id) => String(id).trim().toLowerCase()).filter(Boolean)),
    [organizationAccessUuids]
  );
  const selectedSiteIds = useMemo(
    () => new Set((siteAccessUuids || []).map((id) => String(id).trim().toLowerCase()).filter(Boolean)),
    [siteAccessUuids]
  );

  const visibleOrgs = useMemo(() => {
    const query = String(orgSearch || '').trim().toLowerCase();
    return (organizations || []).filter((org) => {
      const id = String(org?.id || '').trim();
      if (!id) return false;
      if (!query) return true;
      return String(org?.name || '').toLowerCase().includes(query);
    });
  }, [organizations, orgSearch]);

  const visibleSites = useMemo(() => {
    const query = String(siteSearch || '').trim().toLowerCase();
    return (sites || []).filter((site) => {
      const siteId = String(site?.id || '').trim();
      if (!siteId) return false;
      const siteOrgId = String(site?.orgId || '').trim().toLowerCase();
      const selected = selectedSiteIds.has(siteId.toLowerCase());
      const inScope = Boolean(allOrganizationsAccess)
        || !siteOrgId
        || selectedOrgIds.has(siteOrgId)
        || selected;
      if (!inScope) return false;
      if (!query) return true;
      const label = `${site?.name || ''} ${site?.orgName || ''}`.toLowerCase();
      return label.includes(query);
    });
  }, [sites, siteSearch, allOrganizationsAccess, selectedOrgIds, selectedSiteIds]);

  const sitesEnabled = Boolean(allOrganizationsAccess) || selectedOrgIds.size > 0;
  const sitesDisabled = disabled || !sitesEnabled;

  const patch = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const clearSiteSelection = () => ({
    allSitesAccess: false,
    siteAccessUuids: [],
  });

  return (
    <>
      <section className="users-orgs-add-invite-card" aria-labelledby={`${orgSearchId}-heading`}>
        <div className="users-orgs-add-invite-card-head">
          <h3 id={`${orgSearchId}-heading`} className="users-orgs-add-invite-card-title">
            Assign organizations
          </h3>
          <p className="users-orgs-org-assign-hint">Choose the organizations this user can access.</p>
        </div>
        <div className="users-orgs-user-detail-field users-orgs-add-invite-card-body">
          <label className="users-orgs-org-assign-option users-orgs-org-assign-option--all">
            <input
              type="checkbox"
              checked={Boolean(allOrganizationsAccess)}
              disabled={disabled}
              onChange={(e) => {
                const nextAllOrgs = e.target.checked;
                patch({
                  allOrganizationsAccess: nextAllOrgs,
                  ...(nextAllOrgs ? {} : (selectedOrgIds.size === 0 ? clearSiteSelection() : {})),
                });
              }}
            />
            <span><strong>All organizations</strong></span>
          </label>
          <input
            id={orgSearchId}
            type="search"
            className="users-orgs-user-detail-input users-orgs-org-assign-search"
            value={orgSearch}
            onChange={(e) => onOrgSearchChange?.(e.target.value)}
            placeholder="Search organizations"
            autoComplete="off"
            disabled={disabled || Boolean(allOrganizationsAccess)}
          />
          <div className="users-orgs-org-assign-list" role="group" aria-label="Assign organizations">
            {organizations.length === 0 ? (
              <p className="users-orgs-org-assign-empty">No organizations available.</p>
            ) : visibleOrgs.length === 0 ? (
              <p className="users-orgs-org-assign-empty">No organizations match that search.</p>
            ) : (
              visibleOrgs.map((org) => {
                const orgId = String(org.id).trim();
                const checked = selectedOrgIds.has(orgId.toLowerCase());
                return (
                  <label key={orgId} className="users-orgs-org-assign-option">
                    <input
                      type="checkbox"
                      checked={checked || Boolean(allOrganizationsAccess)}
                      disabled={disabled || Boolean(allOrganizationsAccess)}
                      onChange={(e) => {
                        const nextChecked = e.target.checked;
                        const currentIds = Array.isArray(organizationAccessUuids) ? organizationAccessUuids : [];
                        const nextOrgIds = nextChecked
                          ? Array.from(new Set([...currentIds.map(String), orgId]))
                          : currentIds.map(String).filter((id) => id.toLowerCase() !== orgId.toLowerCase());
                        const nextSiteIds = nextChecked
                          ? (Array.isArray(siteAccessUuids) ? siteAccessUuids : [])
                          : (siteAccessUuids || []).filter((siteId) => {
                            const site = (sites || []).find((row) => String(row.id).toLowerCase() === String(siteId).toLowerCase());
                            return site ? String(site.orgId || '').toLowerCase() !== orgId.toLowerCase() : true;
                          });
                        const noOrgsLeft = nextOrgIds.length === 0 && !allOrganizationsAccess;
                        patch({
                          organizationAccessUuids: nextOrgIds,
                          siteAccessUuids: noOrgsLeft ? [] : nextSiteIds,
                          ...(noOrgsLeft ? { allSitesAccess: false } : {}),
                        });
                      }}
                    />
                    <span><strong>{org.name || orgId}</strong></span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </section>

      <section
        className={`users-orgs-add-invite-card${sitesDisabled ? ' users-orgs-add-invite-card--disabled' : ''}`}
        aria-labelledby={`${siteSearchId}-heading`}
        aria-disabled={sitesDisabled}
      >
        <div className="users-orgs-add-invite-card-head">
          <h3 id={`${siteSearchId}-heading`} className="users-orgs-add-invite-card-title">
            Assign sites
          </h3>
          <p className="users-orgs-org-assign-hint">
            {sitesEnabled
              ? 'Sites stay within the organizations selected above.'
              : 'Select one or more organizations first to assign sites.'}
          </p>
        </div>
        <div className="users-orgs-user-detail-field users-orgs-add-invite-card-body">
          <label className="users-orgs-org-assign-option users-orgs-org-assign-option--all">
            <input
              type="checkbox"
              checked={Boolean(allSitesAccess) && sitesEnabled}
              disabled={sitesDisabled}
              onChange={(e) => patch({
                allSitesAccess: e.target.checked,
              })}
            />
            <span><strong>All sites</strong></span>
          </label>
          <input
            id={siteSearchId}
            type="search"
            className="users-orgs-user-detail-input users-orgs-org-assign-search"
            value={sitesEnabled ? siteSearch : ''}
            onChange={(e) => onSiteSearchChange?.(e.target.value)}
            placeholder={sitesEnabled ? 'Search sites' : 'Select an organization first'}
            autoComplete="off"
            disabled={sitesDisabled || Boolean(allSitesAccess)}
          />
          <div
            className={`users-orgs-org-assign-list${sitesDisabled ? ' users-orgs-org-assign-list--disabled' : ''}`}
            role="group"
            aria-label="Assign sites"
          >
            {!sitesEnabled ? (
              <p className="users-orgs-org-assign-empty">Select one or more organizations to see sites.</p>
            ) : visibleSites.length === 0 ? (
              <p className="users-orgs-org-assign-empty">No sites match the selected organizations.</p>
            ) : (
              visibleSites.map((site) => {
                const siteId = String(site.id).trim();
                const checked = selectedSiteIds.has(siteId.toLowerCase());
                const siteOrgId = String(site.orgId || '').trim();
                const orgName = organizations.find((org) => String(org.id).toLowerCase() === siteOrgId.toLowerCase())?.name
                  || site.orgName
                  || '';
                return (
                  <label key={siteId} className="users-orgs-org-assign-option">
                    <input
                      type="checkbox"
                      checked={checked || Boolean(allSitesAccess)}
                      disabled={sitesDisabled || Boolean(allSitesAccess)}
                      onChange={(e) => {
                        const nextChecked = e.target.checked;
                        const currentSiteIds = Array.isArray(siteAccessUuids) ? siteAccessUuids : [];
                        const nextSiteIds = nextChecked
                          ? Array.from(new Set([...currentSiteIds.map(String), siteId]))
                          : currentSiteIds.map(String).filter((id) => id.toLowerCase() !== siteId.toLowerCase());
                        const currentOrgIds = Array.isArray(organizationAccessUuids) ? organizationAccessUuids : [];
                        const nextOrgIds = nextChecked && siteOrgId && !allOrganizationsAccess
                          ? Array.from(new Set([...currentOrgIds.map(String), siteOrgId]))
                          : currentOrgIds.map(String);
                        patch({
                          siteAccessUuids: nextSiteIds,
                          organizationAccessUuids: nextOrgIds,
                        });
                      }}
                    />
                    <span>
                      <strong>{site.name || siteId}</strong>
                      {orgName ? <span>{orgName}</span> : null}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export function buildUserScopeCreatePayload(form, organizationRows = []) {
  const allOrganizationsAccess = Boolean(form?.allOrganizationsAccess);
  const allSitesAccess = Boolean(form?.allSitesAccess);
  const organizationAccessUuids = allOrganizationsAccess
    ? []
    : (Array.isArray(form?.organizationAccessUuids) ? form.organizationAccessUuids.map(String).filter(Boolean) : []);
  const siteAccessUuids = allSitesAccess
    ? []
    : (Array.isArray(form?.siteAccessUuids) ? form.siteAccessUuids.map(String).filter(Boolean) : []);

  let organization = organizationAccessUuids[0] || '';
  if (!organization && allOrganizationsAccess) {
    organization = String(organizationRows[0]?.id || '').trim();
  }

  return {
    organization,
    all_organizations_access: allOrganizationsAccess,
    all_sites_access: allSitesAccess,
    organization_access_uuids: organizationAccessUuids,
    site_access_uuids: siteAccessUuids,
  };
}

export function hasUserScopeSelection(form) {
  if (Boolean(form?.allOrganizationsAccess)) return true;
  return Array.isArray(form?.organizationAccessUuids) && form.organizationAccessUuids.some((id) => String(id || '').trim());
}

export const EMPTY_USER_SCOPE_FORM = {
  allOrganizationsAccess: false,
  allSitesAccess: false,
  organizationAccessUuids: [],
  siteAccessUuids: [],
};
